import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';

import ActionMan from '../../plumbing/ActionMan';
import KStatus from '../../base/data/KStatus';
import GreenTag from '../../base/data/GreenTag';
import DataStore, { getDataPath } from '../../base/plumbing/DataStore';
import C from '../../C';
import Impact from '../../base/data/Impact';
import { A, modifyPage } from '../../base/plumbing/glrouter';
import { bytes, commaList, encURI, isHTML, isURL, space } from '../../base/utils/miscutils';
import PropControl from '../../base/components/PropControl';
import { isTester } from '../../base/Roles';
import Campaign from '../../base/data/Campaign';
import DevOnly from '../../base/components/DevOnly';
import PortalLink from '../../base/components/PortalLink';
import DataItemBadge from '../../base/components/DataItemBadge';
import SavePublishDeleteEtc from '../../base/components/SavePublishDeleteEtc';

import { KAdFormat } from '../../base/data/Advert';
import { gdi, saveTag, setTagPropsFromUser, getDynamicImpactDebits, getImpactTemplates, setDynamicImpactDebits } from './greenTagPageUtils';
import { NoTagYet, TagDetails } from './GreenTagWidgets';
import Misc from '../MiscOverrides';
import LinkOut from '../../base/components/LinkOut';
import printer from '../../base/utils/printer';
import PromiseValue from '../../base/promise-value';
import ServerIO from '../../plumbing/ServerIO';
import JSend from '../../base/data/JSend';
import { getDataItem } from '../../base/plumbing/Crud';
import NGO from '../../base/data/NGO';
import Icon from '../../base/components/Icon';


/**
 * Don't let the user save a non-functional tag!
 * Audit a tag for breaking problems that mean it won't function properly.
 * @param {GreenTag} tag
 * @returns {Object[]} [ {key: 'problem-key', desc: <>Description of problem</>} ]
 */
const problemsForTag = tag => {
	const pvAdvertiser = tag.vertiser && gdi(C.TYPES.Advertiser, tag.vertiser);
	const pvCampaign = tag.campaign && gdi(C.TYPES.Campaign, tag.campaign);

	// Don't let the user save a non-functional tag
	const problems = []; // jsx fragments
	if (!tag.name) {
		problems.push({ key: 'no-name', desc: <>Enter a name for this Green Ad Tag.</> });
	}
	if (!tag.vertiser || !pvAdvertiser.value) {
		problems.push({ key: 'no-brand', desc: <>Choose or create a brand for this tag.</> });
	}
	if (!tag.campaign || !pvCampaign.value) {
		problems.push({ key: 'no-campaign', desc: <>Choose or create a campaign for this tag.</> });
	}
	if (!tag.creativeURL && !tag.creativeHtml && !tag.duration) {
		const desc = (tag?.format === KAdFormat.video) ? <>
			Provide a VAST tag and/or the video duration.
		</> : <>
			Enter a creative URL or tag to enable the Good-Loop Media Lab to measure your advert&apos;s carbon impact.
		</>;
		problems.push({ key: 'no-creative', desc });
	}

	return problems;
};


const MB = 1024*1024;


/**
 * What type of creative does the given string or Green Ad Tag represent - URL or HTML fragment?
 * @param {Object} p Props object
 * @param {GreenTag} p.tag A Green Ad Tag with one or more of "creativeURL" and "creativeHTML" (URL takes priority)
 * @param {string} p.creativeText A string which might be a URL or HTML fragment
 * @returns {string|null} "url", "html", or null if detection fails for both.
 */
const guessCreativeKind = ({ creativeText, tag }) => {
	if (tag?.creativeURL || isURL(creativeText)) return 'url';
	if (tag?.creativeHtml || isHTML(creativeText)) return 'html';
	return null;
};


/** Types of creative EditCreative accepts & labels for them */
const creativeKinds = { url: 'Link', html: 'Tag' };


/** Save the creative value to the correct HTML/URL field on the Green Ad Tag & make sure the other field is empty */
const saveCreative = (text, kind, tagPath) => {
	const fields = ['creativeHtml', 'creativeURL'];
	const [targetField, otherField] = (kind === 'html') ? fields : fields.reverse();
	DataStore.setValue([...tagPath, targetField], text);
	DataStore.setValue([...tagPath, otherField], null);
};

/**
 * Present hopefully-intuitive "Paste your creative here, change its type if we guessed wrong" controls
 * - and translate between them and the internal "creativeURL" and "creativeHtml" fields.
 */
function EditCreativeGeneric({ tag, tagPath }) {
	const [creativeText, setCreativeText] = useState(); // The creative string, whatever it is
	const [creativeKind, setCreativeKind] = useState(); // "url" or "html"
	const [kindGuess, setKindGuess] = useState(); // Store autoselected kind so we can flag if it looks like the user's overridden it wrong
	const [kindSource, setKindSource] = useState(); // Where did this component get creativeKind from? (So we don't override a user-picked value)

	const onChangeText = (e) => setCreativeText(e.target.value);
	const onChangeKind = (e) => {
		setCreativeKind(e.target.value);
		setKindSource('user'); // Flag user-set value & protect it from automatic overwrite
	};

	// On mount or tag change, translate the tag's fields to the control values
	useEffect(() => {
		const text = tag.creativeURL || tag.creativeHtml;
		if (!text) return;
		setCreativeText(text);
		setCreativeKind(guessCreativeKind({ tag }));
		setKindSource('saved');
	}, [tag?.id]);

	// When creative text changes, try to guess if it's a URL or HTML fragment.
	useEffect(() => {
		const guess = guessCreativeKind({ creativeText });
		setKindGuess(guess);
		// Don't overwrite user-picked kind!
		const nextKind = (kindSource === 'user') ? creativeKind : guess;
		// Always save changed creative back to tag
		saveCreative(creativeText, nextKind, tagPath);
		// Don't update unnecessarily
		if (nextKind === creativeKind) return;
		setCreativeKind(nextKind);
		setKindSource('guess');
	}, [creativeText]);

	// When creative kind changes, put the text in one of "creativeURL" or "creativeHtml" and clear the other one.
	useEffect(() => {
		saveCreative(creativeText, creativeKind, tagPath);
	}, [creativeKind]);

	// Flag issues to the user
	let info = [];
	// Tell the user kind is currently autoset (keep this first, goes inline with radio buttons)
	if (creativeKind && (kindSource === 'guess')) {
		info.push(<FormGroup inline check>
			<Label>(Auto-detected)</Label>
		</FormGroup>);
	}
	// User has (probably) marked a tag as a link, or vice versa - offer to correct
	if (creativeKind && kindGuess && creativeKind !== kindGuess) {
		const autofix = () => {
			setCreativeKind(kindGuess);
			setKindSource('guess');
		};
		info.push(<Alert color="warning" className="d-flex justify-content-between">
			This looks like a {creativeKinds[kindGuess].toLowerCase()},<br/>
			but its type is set to "{creativeKinds[creativeKind]}".
			<Button color="primary" onClick={autofix}>Fix</Button>
		</Alert>);
	}
	// Autodetect has failed, kind needs to be manually set
	if (creativeText && !creativeKind) {
		info.push(<Alert color="warning" key="autodetect-fail">
			Couldn't autodetect creative type - please select "Link" or "Tag".
		</Alert>);
	}

	return <div className="mb-2">
		<FormGroup className="mb-1">
			<Label>Creative preview link or HTML tag</Label>
			<Input type="text" onChange={onChangeText} value={creativeText} />
		</FormGroup>
		{Object.entries(creativeKinds).map(([kind, label]) => <FormGroup inline check key={kind}>
			<Label check>
				<Input type="radio" value={kind} checked={creativeKind === kind} onChange={onChangeKind} /> {label}
			</Label>
		</FormGroup>)}
		{info}
	</div>;
}


/** PropControl help element for the "enter video URL" input */
const creativeHelp = (tagPath, isVideo) => {
	const setExample = () => {
		DataStore.setValue([...tagPath, 'creativeURL'], 'https://example.com');
	};
	return <>
		Provide a link to your creative, or a HTML insertion tag, so the media lab can weigh it. This may be:<br/>
		<ul>
			<li>a VAST tag</li>
			<li>a preview link from your DSP</li>
			{isVideo ? <li>
				<i>Do not provide an upload of the video file.</i> Adservers recompress video files.
			</li> : <li>
				or an upload of the creative to a file sharing site like WeTransfer or DropBox.
			</li>}
		</ul>
		If you&apos;re stuck, <Button size="sm" onClick={setExample}>enter https://example.com</Button> and email support@good-loop.com.
	</>;
};


/**
 * Present the simplified controls that a tag with creative type "video" uses
 */
function EditCreativeVideo({ tagPath }) {
	/* NB: Because video usually gets re-compressed by the adservers, the size of
	a raw video file isn't that useful. Using video length will be more accurate.
	Note: VAST tags are still worth measuring as creatives -- since (a) a VAST tag
	can carry arbitrary extra bumpf alongside the video, and (b) we view VAST tags
	via an adserver, so we get to see the compression. */
	return <>
		<PropControl type="dt" prop="duration" path={tagPath} unitOptions={['SECOND']} 
			label="Video length" help="How long is this video?"
		/>
		<PropControl type="url" prop="creativeURL" path={tagPath}
			label="Link to the advert creative" help={creativeHelp(true)}
		/>
	</>;
}


/**
 * Show an appropriate set of controls for setting up the creative on a Green Ad Tag.
 */
function EditCreative({ tag, tagPath }) {
	const EditComponent = (tag?.format === 'video') ? EditCreativeVideo : EditCreativeGeneric;
	return <EditComponent tag={tag} tagPath={tagPath} />;
}


/** Creative measurement controls & links to manual tools. */
function EditMeasurement({ tag, tagPath }) {
	// TODO status:exploring measure the creative. What about VAST tags?
	const [pvMeasure, setPvMeasure] = useState();

	const creative = tag.creativeURL || tag.creativeHtml;

	// Call MeasureServlet2 for auto-measurement
	const doAutoMeasure = () => {
		// TODO Is the use of "tag" correct?
		const data = {tag: creative, why: 'measure-tag', format: tag.format};
		let pMeasure = ServerIO.load(`${ServerIO.MEASURE_ENDPOINT}2`, {data});
		setPvMeasure(new PromiseValue(pMeasure));
		pMeasure.then(() => DataStore.update()); // redraw when we get the info back
	};
	const measured = pvMeasure?.value && JSend.data(pvMeasure.value);

	// Apply the measurement from MeasureServlet2 to the GAT
	const useMeasurement = () => DataStore.setValue([...tagPath, 'weight'], measured.advert.bytes);

	let buttonText = 'Auto-Measure';
	let buttonDisabled = !creative || !tag.format;
	if (pvMeasure && !pvMeasure.resolved) {
		buttonText = 'Measuring…';
		buttonDisabled = true;
	} else if (tag.weight) {
		buttonText = 'Redo Auto-Measure';
	}

	return <>
		<PropControl label="Creative size (bytes)" type="number" path={tagPath} prop="weight" min={0} step={1} int
			help="Size of the creative that gets delivered. Best practice: measure from the ad-tag, or a preview page for the ad-tag. Note: it's not the size of raw assets, but the size of data that gets sent out. E.g. raw video files are usually encoded to be smaller." />
		<div>
			<Button color={measured ? 'default' : 'primary'} onClick={doAutoMeasure} disabled={buttonDisabled}>{buttonText}</Button>
			{measured && <Row>
				<Col><Misc.ImgThumbnail url={measured.screenshot} /></Col>
				<Col>
					<div>{measured.domain}</div>
					<div title={`${measured.advert.bytes} bytes`}>{bytes(measured.advert.bytes)}</div>
					<Button onClick={useMeasurement}>Use this</Button>
				</Col>
			</Row>}
			<A href={`/#measure?tagId=${encURI(tag.id)}`}></A>
		</div>
		<div className="mb-2">
			<LinkOut href="https://calstat.good-loop.com/#green/measure">VAST Tag Measure Tool</LinkOut>
		</div>
	</>;
}


/** Minimal editor for setting up basic props on an Advertiser during GAT setup */
function QuickEditBrandModal({ id }) {
	const [open, setOpen] = useState(false);
	const toggle = () => setOpen(val => !val);
	if (!id) return null;

	const advertiser = id && gdi(C.TYPES.Advertiser, id).value;
	const advPath = id && getDataPath({ status: KStatus.DRAFT, type: C.TYPES.Advertiser, id });

	return <>
		<a onClick={toggle}><Icon name="edit" /></a>
		<Modal isOpen={open} toggle={toggle} className="edit-brand-modal">
			<ModalHeader toggle={toggle}>Quick Edit {advertiser?.name || 'Brand'}</ModalHeader>
			<ModalBody>
				<PropControl label="Brand name" path={advPath} prop="name" type="text"/>
				<PropControl label="Brand logo" path={advPath.concat("branding")} prop="logo" type="imgUpload"/>
				<SavePublishDeleteEtc autoSave oneButton sendDiff id={id} type={C.TYPES.Advertiser} />
			</ModalBody>
		</Modal>
	</>;
}


/**
 * Resolve an impact debit ID to e.g. "Tree planting by TreePlanters™"
 * @param {?string} impactDebitId
 * @param {?ImpactDebit[]} impactdTemplates
 * @returns {!string}
 */
const impactLabelFn = (impactDebitId, impactdTemplates) => {
	if (!impactDebitId) return '';

	if (impactdTemplates) {
		const impd = impactdTemplates.find(_impd => _impd.id === impactDebitId);
		const cid = impd.impact.charity;
		const charity = cid && getDataItem({type: 'NGO', id: cid, status: KStatus.PUBLISHED}).value;

		const impdName = impd.impact?.name || impd.name || impactDebitId;
		if (!charity) return impdName; // "[Impact name]" only
		const cName = charity ? NGO.displayName(charity) : cid;
		return `${impdName} by ${cName}`; // "[Impact name] by [Charity Name]"
	}

	// if (impactName.match(/^carbon/)) return 'Offset only'; // NB: s/carbon/offset/ for display
	return impactDebitId;
};


/** Edit the impacts/offsets attached to this Green Ad Tag's campaign. */
function EditImpacts({ tag, isNew }) {
	// Fetch campaign if the GAT has one
	const campaign = tag?.campaign && gdi(C.TYPES.Campaign, tag.campaign).value;

	// impacts set - make sure campaign is fetched first to avoid race condition below attempting to set default on null campaign
	let impactDebits = campaign && getDynamicImpactDebits(tag.campaign, C.TYPES.Campaign).value;

	// What impacts can we set for this campaign? Get possible values from the tag's campaign hierarchy.
	const impactdTemplates = getImpactTemplates(tag);
	const impactOptions = impactdTemplates ? impactdTemplates.map(it => it.impact.name) : [];
	if (impactdTemplates && !impactOptions.length) {
		// Shouldn't happen after loading - TOTAL_IMPACT should always provide a fallback
		console.warn('Loaded dynamic ImpactDebits but no Impact options!');
	}

	// No impacts selected? Default to whatever CO2 offset is available.
	if (impactdTemplates && impactDebits && !impactDebits.length && isNew) {
		const impdco2 = impactdTemplates.find(impd => Impact.isCarbonOffset(impd.impact));
		if (!impdco2) {
			console.warn('No default co2?!', impactdTemplates);
		} else {
			console.log('set default co2', impdco2);
			setDynamicImpactDebits(campaign, [impdco2.impact.name], impactdTemplates);
			impactDebits = [impdco2]; // NB: technically this should be the clone of impdco2 but whatevs -- the usage below only wants on/off names
		}
	}

	// a list of on/off names for the PropControl below
	// HACK setDynamicImpactDebits caches the value for responsive controls
	const onIds = campaign?._impactIds || (impactDebits||[]).map(impd => impd.id);
	// Keep the "dummy-impact" field out of the GreenTag object
	const impactNamesPath = ['widget', 'GreenTagEditor', tag.id || 'meh'];
	DataStore.setValue(impactNamesPath.concat('impactIds'), onIds, false);

	// When the checkbox PropControl changes, update the Campaign linked to the GAT
	const onChangeImpacts = ({value, _path}) => {
		console.log('new impact setup', value, _path, 'pvc', campaign);
		Campaign.assIsa(campaign);
		setDynamicImpactDebits(campaign, value, impactdTemplates);
	};

	const unsets = [];
	if (!tag?.agencyId) unsets.push('Agency');
	if (!tag?.vertiser) unsets.push('Brand');
	if (!tag?.campaign) unsets.push('Campaign');
	const warnUnsets = unsets.length ? <span>Set {commaList(unsets)}</span> : null;

	return <>
		<PropControl label="Campaign Impacts" type="checkboxes" disabled={!campaign}
			path={impactNamesPath} prop="impactIds" /* TODO refactor to use impactSuppliers */
			options={impactdTemplates? impactdTemplates.map(impd => impd.id) : []}
			labels={impdId => impactLabelFn(impdId, impactdTemplates)}
			help="Will this Green Ad Campaign only be purchasing carbon offsets, or will it have other impacts as well?"
			saveFn={onChangeImpacts}
		/>
		{/* warnUnsets */}
		{warnUnsets || (!impactdTemplates ? <span>...</span> : null)}
		<DevOnly>
			Templates:<br/>
			{impactdTemplates ? (
				impactdTemplates.map(impd => <DataItemBadge key={impd.id} item={impd} />)
			) : '(No impact templates found)'}
			Selected:<br/>
			{impactDebits ? (
				impactDebits.map(impd => <DataItemBadge key={impd.id} item={impd} href />)
			) : '(No impacts selected)'}
		</DevOnly>
		{campaign?.id && <SavePublishDeleteEtc autoSave autoPublish hidden sendDiff id={campaign.id} type={C.TYPES.Campaign} />}
	</>;
}


const XRefControl = ({ campaignId }) => {
	const cpath = campaignId && getDataPath({status: KStatus.DRAFT, type: C.TYPES.Campaign, id: campaignId });

	let help = 'Optional. Use this e.g. to record a link with an account or a deal in your CRM';
	if (!cpath) help = 'First set a Campaign. ' + help;

	return <PropControl label="Your campaign reference" disabled={!cpath} path={cpath} prop="xref"
		help={help}
	/>;
};


function GreenTagEditor({ tagId, type, path, goBack}) {
	const isNew = (tagId === 'new');

	// Get a clean copy of the tag, or create it
	const pvTag = isNew ? (
		{ resolved: true, value: new GreenTag() }
	) : (
		ActionMan.getDataItem({ type, status: KStatus.PUBLISHED, id: tagId })
	);

	// Tag not loaded?
	if (!pvTag.value) return <NoTagYet pvTag={pvTag} tagId={tagId} />;

	// Check existence of draft copy
	let tag = DataStore.getValue(path);
	if (!tag) {
		// ...and create if necessary.
		tag = new GreenTag(pvTag.value);
		// HACK Fill in ID for just-created "new" tag
		if (!tag.id) tag.id = tagId;
		// HACK set the agency and advertiser: If one-and-only-one agency has been shared with the user, then set that
		setTagPropsFromUser(tag);
		DataStore.setValue(path, tag, false);
	}

	// Don't let the user save a non-functional tag!
	const problems = problemsForTag(tag);
	const noSave = !!problems.length;

	// Delete button action: Confirm first.
	const doDelete = () => {
		if (!window.confirm(`Are you sure you want to delete "${tag.name}"?`)) return;
		ActionMan.delete(C.TYPES.GreenTag, tagId);
		modifyPage(['green']);
	};

	return (<>
		{!isNew && <>
			<TagDetails tag={tag} editing />
			<XRefControl campaignId={tag.campaign} />
		</>}
		<PropControl label="Agency" path={path} prop="agencyId" type="DataItem" pageSize={10} itemType={C.TYPES.Agency} status={KStatus.PUBLISHED}
			notALink={!isTester()}
		/>
		{isNew && <>
			<PropControl label="Brand" path={path} prop="vertiser" type="DataItem" pageSize={10} itemType={C.TYPES.Advertiser} required
				status={KStatus.PUB_OR_DRAFT} canCreate base={{agencyId:tag.agencyId}} createProp="name"
				notALink={!isTester()} customIcon={<QuickEditBrandModal id={tag.vertiser} />}
			/>
			<PropControl label="Campaign" path={path} prop="campaign" type="DataItem" pageSize={10} itemType={C.TYPES.Campaign} required
				status={KStatus.PUB_OR_DRAFT}
				disabled={!tag.vertiser}
				canCreate base={{agencyId:tag.agencyId, vertiser:tag.vertiser}} createProp="name"
				notALink={!isTester()}
				help="Use a common campaign ID to collate logging across multiple tags."
			/>
			<XRefControl campaignId={tag.campaign} />
		</>}
		<EditImpacts tag={tag} isNew={isNew} />
		<PropControl required label={isNew ? 'Enter a name for your Green Ad Tag' : 'Name'} path={path} prop="name" />
		<PropControl label="Creative format" type="select" path={path} prop="format" options={[KAdFormat.display, KAdFormat.video]} />
		<EditCreative tag={tag} tagPath={path} isNew={isNew} />
		<DevOnly test>
			<EditMeasurement tag={tag} tagPath={path} isNew={isNew} />
		</DevOnly>
		{problems.map(({key, desc}) => <Alert key={key} color="warning" className="px-2 py-1 mb-2">{desc}</Alert>) }
		<Button color="primary" className="mr-1" onClick={() => saveTag(tag)} disabled={noSave}>
			{isNew ? 'Create Tag' : 'Save Tag'}
		</Button>
		<Button color="default" onClick={goBack}>Cancel</Button>
		<Button color="danger" className="pull-right" onClick={doDelete}>Delete</Button>
	</>);
} // ./EditPane

export default GreenTagEditor;
